import React, { useMemo, useState } from 'react';

// Types.
import HostTarget from '../../../types/host/HostTarget';
import ChartPlotLine from '../../../types/ChartPlotLine';

// Constants.
import { CHART_DATA_TYPE, CHART_COLOURS_STATEMENTS, VIEW_MODE } from '../../Constants';

import ConditionalRender from '../../../helpers/ConditionalRender';

import TimeSeriesChart from '../../chart/echarts/TimeSeriesChart';
import { EChartTimeSeries } from '../../chart/echarts/types';
import HostChartCard from './HostChartCard';
import { toMetricSeries, toSeriesPoints, useHostStatistic } from './useHostStatistic';

const TRANSFER_METRICS = ['K read', 'K written'];
const COMPLETED_METRICS = ['reads completed', 'writes completed', 'I/Os currently in progress'];
const TIME_METRICS = ['time spent reading (ms)', 'time spent writing (ms)', 'time spent doing I/Os (ms)', 'weighted time spent doing I/Os (ms)'];
const TIME_LINE_METRICS = ['time spent doing I/Os (ms)', 'weighted time spent doing I/Os (ms)'];

function DiskChart(props: { host: HostTarget, plotLines?: ChartPlotLine[] }) {
    const [mode, setMode] = useState<number>(VIEW_MODE.BASIC);
    const { loading, rows, timeSeries, peak: timePeak } = useHostStatistic(props.host, 'ioutilisation');
    const basicSeries = useMemo<EChartTimeSeries[]>(() => [{
        name: 'Average disk I/O',
        chartType: 'line',
        data: timeSeries,
    }], [timeSeries]);

    const transferSeries = useMemo<EChartTimeSeries[]>(() => toMetricSeries(rows, TRANSFER_METRICS), [rows]);

    const completedSeries = useMemo<EChartTimeSeries[]>(() => toMetricSeries(rows, COMPLETED_METRICS), [rows]);

    const timeSpentSeries = useMemo<EChartTimeSeries[]>(() => TIME_METRICS.map((metric, index) => {
        const isLine = TIME_LINE_METRICS.includes(metric);
        return {
            name: metric,
            chartType: isLine ? 'line' : 'bar',
            color: CHART_COLOURS_STATEMENTS[index],
            data: toSeriesPoints(rows, metric),
            yAxisIndex: isLine ? 1 : 0,
        } as EChartTimeSeries;
    }), [rows]);

    return (
        <HostChartCard
            cardTitle="Average I/O over time"
            chartTitle="Average I/O"
            collapseId="collapseDiskCharts"
            peakLabel="Peak Average I/O"
            peakValue={timePeak}
            peakSuffix="%"
            mode={mode}
            onToggleMode={() => setMode(mode === VIEW_MODE.BASIC ? VIEW_MODE.DETAIL : VIEW_MODE.BASIC)}
            loading={loading}
        >
            <ConditionalRender if={mode === VIEW_MODE.BASIC}>
                <TimeSeriesChart series={basicSeries} tooltipType={CHART_DATA_TYPE.PERCENTAGE}
                                 plotLines={props.plotLines}/>
            </ConditionalRender>

            <ConditionalRender if={mode === VIEW_MODE.DETAIL}>
                <React.Fragment>
                    <TimeSeriesChart series={transferSeries} tooltipType={CHART_DATA_TYPE.GENERIC}
                                     plotLines={props.plotLines} legend={true}/>
                    <br/>
                    <TimeSeriesChart series={completedSeries} tooltipType={CHART_DATA_TYPE.GENERIC}
                                     plotLines={props.plotLines} legend={true}/>
                    <br/>
                    <TimeSeriesChart series={timeSpentSeries} tooltipType={CHART_DATA_TYPE.GENERIC}
                                     plotLines={props.plotLines} legend={true}/>
                </React.Fragment>
            </ConditionalRender>
        </HostChartCard>
    );
}

export default DiskChart;
